import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../api";
import { onQueueChange, queueLength } from "../offlineQueue";
import LegalFooter from "./LegalFooter";
import { DPA_VERSION } from "../dpaText";

const DPA_NOTICE_KEY = "dpa_notice_dismissed_" + DPA_VERSION;

// Bump this for every deployed change: patch (5.0.1, 5.0.2, ...) for
// incremental fixes/additions, minor (5.1, 5.2, ...) for a batch of changes
// substantial enough to be considered a new version milestone.
export const APP_VERSION = "7.7.3";

// Genuine team-vs-team formats only — the whole field splits into 2 named
// sides and results aggregate into two grand totals. See PAIR_FORMATS below
// for formats where a 2-player pair, not a club-wide side, is the unit.
// Widened 2026-09-06: the 4 pair-vs-pair match play formats can ALSO be run
// inter-club (named sides, not just ad-hoc pairs) — Mike confirmed live use
// of the Teams page for Better Ball Match Play, so the Teams nav link must
// stay reachable for these too, not just the 5 pure named-team formats.
const TEAM_FORMATS = ["Team Stableford", "Team Medal", "Team Aggregate Stableford", "Team Aggregate Stroke Play", "Team Match Play", "Better Ball Match Play", "Fourball Match Play", "Foursomes Match Play", "Greensomes Match Play"];

// Pair formats — partnership only matters within each fourball group, no
// club-wide split, no named sides. Pairs are generated automatically by
// Auto Group (balanced by handicap), viewed on the Pairs page.
// Added 2026-10-18: Team Stableford (Best 2 of 4) -- scoring itself only
// reads the plain grp field (see terminal.js/scores.js TEAM_FORMAT_CONFIG,
// unaffected by this), but real-world play is two self-organised pairs
// joining to fill a fourball for an open competition, which needs the
// Pairs page's drag/drop + tee times/buggy/Auto Group tools, not the bare
// numeric Groups page.
const PAIR_FORMATS = [
  "Better Ball Stableford", "Better Ball Medal", "Better Ball Stroke Play", "Better Ball Match Play",
  "Fourball Better Ball Stableford", "Fourball Match Play", "Fourball Stroke Play",
  "Foursomes Stableford", "Foursomes Medal", "Foursomes Match Play",
  "Greensomes Stableford", "Greensomes Medal", "Greensomes Match Play",
  "Team Stableford (Best 2 of 4)"
];

// "Group Winner" only makes sense where players compete as individuals but
// a secondary group-of-4 prize (best 2 scores per hole) is also being run —
// a genuine singles-society tradition. It does NOT apply to Match Play
// (won/lost/halved, not a points total), Skins, or any Team/Pair format —
// those already have their own dedicated results pages.
const GROUP_WINNER_FORMATS = [];

// section: which collapsible sidebar group an item renders under (2026-10-21
// redesign, Mike's own design — see [[gsm_sidebar_redesign_2026_10]]). icon
// is a standalone glyph rendered in its own column now, so labels no longer
// carry an emoji prefix inline the way a couple of them used to.
const allNavItems = [
  { label: "User Manual", path: "/manual", manual: true },
  { label: "Marker Verify Guide", path: "/manual#marker-verify", guide: true },
  { label: "Download Offline App", path: "https://apps.microsoft.com/detail/xpfp0h5dz71t5r?hl=en-GB&gl=GB", download: true, hideForElectron: true },
  { label: "Dashboard", path: "/players", icon: "👥", section: "players" },
  { label: "Player Lists", path: "/player-lists", icon: "📋", section: "players" },
  { label: "Planner", path: "/planner", icon: "📅", section: "players" },
  { label: "Groups", path: "/groups", icon: "👥", section: "groups" },
  { label: "Teams", path: "/teams", teamOnly: true, icon: "👥", section: "groups" },
  { label: "Pairs", path: "/pairs", pairOnly: true, icon: "🔗", section: "groups" },
  { label: "Course", path: "/course", icon: "🚩", section: "groups" },
  { label: "Score Entry", path: "/scores", icon: "✏️", section: "groups" },
  { label: "Results", path: "/results", icon: "🏆", section: "groups" },
  { label: "Leaderboard", path: "/leaderboard", icon: "📊", section: "groups" },
  { label: "Archive", path: "/archive", icon: "🗄️", section: "groups" },
  { label: "Multi Archive", path: "/multiarchive", icon: "🗂️", section: "groups" },
  { label: "Player Stats", path: "/stats", icon: "📈", section: "groups" },
  { label: "Twos & Fines", path: "/twos", icon: "🪙", section: "groups" },
  { label: "Winners & Losers", path: "/winners", icon: "🏅", section: "groups" },
  { label: "Mail Merge", path: "/mailmerge", icon: "✉️", section: "groups" },
  { label: "Vote Results", path: "/vote-results", hideForElectron: true, icon: "🗳️", section: "groups" },
  { label: "Register Player Accounts", path: "/link-accounts", hideForElectron: true, icon: "🔑", section: "groups" },
  { label: "General Play", path: "/general-play", icon: "⛳", section: "groups" },
  { label: "Settings", path: "/settings", hideForElectron: true, icon: "⚙️", section: "groups" },
  { label: "AI Scoring Wizard", path: "/wizard", hideForElectron: true, icon: "🧙", section: "groups" },
];
const NAV_SECTIONS = [
  { key: "players", label: "Players" },
  { key: "groups", label: "Groups" },
];

const isElectron = typeof navigator !== "undefined" && navigator.userAgent.toLowerCase().includes("electron");

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [syncing, setSyncing] = React.useState(false);
  const [syncSeconds, setSyncSeconds] = React.useState(0);
  React.useEffect(() => {
    if (!syncing) { setSyncSeconds(0); return; }
    setSyncSeconds(0);
    setSyncDone(false);
    const id = setInterval(() => setSyncSeconds(s => s + 1), 1000);
    return () => clearInterval(id);
  }, [syncing]);
  const [lastSync, setLastSync] = React.useState(null);
  const [syncError, setSyncError] = React.useState("");
  const [showSyncModal, setShowSyncModal] = React.useState(false);
  const [syncDone, setSyncDone] = React.useState(false);

  const handlePull = async () => {
    setShowSyncModal(false);
    setSyncing(true);
    setSyncError("");
    try {
      await api.post("/sync/pull");
      setLastSync(new Date());
      // A brief, visible "done" confirmation before reloading -- reloading
      // instantly the moment a now-fast (~8s) sync finishes, with no
      // confirmation at all, read as a crash rather than a success.
      setSyncDone(true);
      await new Promise(r => setTimeout(r, 1200));
      // Reload so whichever screen is open re-fetches the freshly synced data —
      // the sync updates the local database directly, but an already-loaded
      // page has no way to know new data just arrived otherwise.
      window.location.reload();
    } catch (e) {
      setSyncError(e.response?.data?.error || "Pull failed — check your internet connection");
      setSyncing(false);
    }
  };
  const handlePush = async () => {
    setShowSyncModal(false);
    setSyncing(true);
    setSyncError("");
    try {
      await api.post("/sync/push");
      setLastSync(new Date());
      setSyncDone(true);
      await new Promise(r => setTimeout(r, 1200));
      window.location.reload();
    } catch (e) {
      setSyncError(e.response?.data?.error || "Push failed — check your internet connection");
      setSyncing(false);
    }
  };
  const [isTeamFormat, setIsTeamFormat] = React.useState(true);
  const [isPairFormat, setIsPairFormat] = React.useState(false);
  const [showGroupWinner, setShowGroupWinner] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [collapsedSections, setCollapsedSections] = React.useState({});
  const toggleSection = (key) => setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  const [showImportDialog, setShowImportDialog] = React.useState(false);
  const [importConfirmText, setImportConfirmText] = React.useState("");
  const [safetyNetFiles, setSafetyNetFiles] = React.useState([]);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) setMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const refreshFormatFlags = React.useCallback(() => {
    api.get("/events/current").then(res => {
      if (res.data && res.data.game_type) {
        setIsTeamFormat(TEAM_FORMATS.includes(res.data.game_type));
        setIsPairFormat(PAIR_FORMATS.includes(res.data.game_type));
        setShowGroupWinner(GROUP_WINNER_FORMATS.includes(res.data.game_type));
      }
    }).catch(() => {});
  }, []);

  React.useEffect(() => {
    refreshFormatFlags();
  }, [location.pathname, refreshFormatFlags]);

  // Layout wraps every page and only re-checks the event's format on
  // navigation (the pathname dependency above) -- changing the Format
  // dropdown on Players.jsx without leaving the page never told the
  // sidebar, so Teams/Pairs stayed greyed out for whatever format was
  // active when the page first loaded. Found live 2026-09-09 (switched to
  // Team Stableford, Teams nav stayed disabled until navigating away and
  // back). Players.jsx dispatches this event right after a game_type save.
  React.useEffect(() => {
    window.addEventListener("gsm-event-format-changed", refreshFormatFlags);
    return () => window.removeEventListener("gsm-event-format-changed", refreshFormatFlags);
  }, [refreshFormatFlags]);

  React.useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const [isOnline, setIsOnline] = React.useState(navigator.onLine);
  const [dpaNoticeDismissed, setDpaNoticeDismissed] = React.useState(() => {
    try { return localStorage.getItem(DPA_NOTICE_KEY) === "true"; } catch { return false; }
  });
  const dismissDpaNotice = () => {
    setDpaNoticeDismissed(true);
    try { localStorage.setItem(DPA_NOTICE_KEY, "true"); } catch { /* ignore */ }
  };
  const [pendingCount, setPendingCount] = React.useState(queueLength());
  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    const unsubscribe = onQueueChange(setPendingCount);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      unsubscribe();
    };
  }, []);



  const [societyInfo, setSocietyInfo] = React.useState(null);
  const [resyncing, setResyncing] = React.useState(false);
  const isPlayground = societyInfo?.email === "playgronds@gsm";

  const handleResyncPlayground = async () => {
    if (!window.confirm("This wipes everything currently in the playground and replaces it with a fresh copy of Pin High's current players and event. Continue?")) return;
    setResyncing(true);
    try {
      const res = await api.post("/playground/resync");
      alert(res.data.message || "Resynced");
      window.location.reload();
    } catch(e) {
      alert("Resync failed: " + (e.response?.data?.error || e.message));
    } finally {
      setResyncing(false);
    }
  };
  const loadSocietyInfo = () => {
    const token = sessionStorage.getItem('token');
    if (!token) { setSocietyInfo(null); return; }
    fetch('/api/auth/me', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setSocietyInfo(data); })
      .catch(() => {});
  };
  React.useEffect(() => {
    loadSocietyInfo();
    // The organiser login page does a full page reload after signing in,
    // which is enough for a single tab. But localStorage is shared across
    // every tab of the same browser — if a DIFFERENT tab logs into another
    // account, it overwrites the one shared token. This tab never reloads,
    // so this sidebar (fetched once above) would otherwise keep showing
    // whichever society was logged in when THIS tab first opened, while
    // every actual data request from this same tab immediately starts
    // using the new, overwritten token — a real account mismatch between
    // what the sidebar claims and whose data is actually on screen. The
    // browser's own 'storage' event fires in exactly this situation (any
    // OTHER tab changing localStorage), so re-fetching on it keeps the
    // sidebar honest without needing a reload. Found live 2026-09-01.
    const onStorageChange = (e) => { if (e.key === 'token') loadSocietyInfo(); };
    window.addEventListener('storage', onStorageChange);
    return () => window.removeEventListener('storage', onStorageChange);
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem("token");
    // Defense in depth alongside the per-token tie in Wizard.jsx: wipe
    // the Wizard key immediately on an explicit logout too, not just
    // the next time the Wizard page checks it.
    localStorage.removeItem("wizard_key");
    localStorage.removeItem("wizard_key_for_token");
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage("clear-api-cache");
    }
    navigate("/login");
  };

  const handleNav = (path, isDisabled) => {
    if (isDisabled) return;
    navigate(path);
    setMenuOpen(false);
  };

  const isMob = isMobile;

  // Export/Import Game (2026-09-15) -- works like Save Game / Load Game
  // (a template snapshot that always creates a brand-new event, never
  // touches an existing one) but as a portable file instead of an internal
  // saved_games row, so it can cross between the website and the offline
  // app's separate database. Deliberately not sync -- one-shot, manual,
  // one direction at a time.
  const exportGame = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch("/api/events/current/export", { headers: { Authorization: "Bearer " + token } });
      if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.error || "Export failed"); return; }
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="(.+)"/);
      const filename = match ? match[1] : "gsm-game-export.json";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch(e) { alert("Export failed: " + e.message); }
  };

  const importGame = () => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "application/json,.json";
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      try {
        const text = await file.text();
        const payload = JSON.parse(text);
        const token = sessionStorage.getItem("token");
        const res = await fetch("/api/events/import", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!res.ok) { alert(data.error || "Import failed"); return; }
        alert(`Imported "${data.event.venue}" — ${data.matched} player(s) matched, ${data.created} new player(s) created, ${data.scoresImported || 0} hole score(s) restored. This is now your active competition.`);
        window.location.reload();
      } catch(e) { alert("Import failed: " + e.message); }
    };
    input.click();
  };

  const exportDatabase = async () => {
    const defaultName = "backup-" + new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
    const token = sessionStorage.getItem("token");
    // Electron: window.prompt() is not implemented by Electron's BrowserWindow
    // the way it is in a real browser tab — calling it here was silently
    // short-circuiting this whole function before the export ever ran, which
    // is why every previous fix downstream (blob download, will-download
    // handler, native IPC) never actually got a chance to execute. The native
    // save dialog in main.js already lets the user name/choose the file, so
    // the Electron path skips window.prompt() entirely.
    if (window.electronAPI?.exportDatabase) {
      const result = await window.electronAPI.exportDatabase(defaultName, token);
      if (result.cancelled) return;
      if (!result.success) alert("Export failed: " + result.error);
      else alert("Database exported to:\n" + result.path);
      return;
    }
    const backupName = window.prompt("Enter backup name:", defaultName);
    if (backupName === null) return;
    const safeName = backupName.replace(/[^a-zA-Z0-9_-]/g, '_') || 'backup';
    try {
      const res = await fetch("/api/database/export?name=" + encodeURIComponent(safeName), {
        headers: { "Authorization": "Bearer " + token }
      });
      if (res.status !== 200) throw new Error("Export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = safeName + ".json";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch(e) { alert("Export failed: " + e.message); }
  };

  const importDatabase = () => {
    setImportConfirmText("");
    setShowImportDialog(true);
    loadSafetyNetFiles();
  };

  const loadSafetyNetFiles = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch("/api/database/safety-net", {
        headers: { "Authorization": "Bearer " + token }
      });
      const data = await res.json();
      setSafetyNetFiles(res.status === 200 ? (data.files || []) : []);
    } catch(e) { setSafetyNetFiles([]); }
  };

  const downloadSafetyNet = async (filename) => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch("/api/database/safety-net/" + encodeURIComponent(filename), {
        headers: { "Authorization": "Bearer " + token }
      });
      if (res.status !== 200) throw new Error("Download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch(e) { alert("Download failed: " + e.message); }
  };

  const doImport = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const formData = new FormData();
        formData.append("sqlfile", file);
        const token = sessionStorage.getItem("token");
        const res = await fetch("/api/database/import", {
          method: "POST",
          headers: { "Authorization": "Bearer " + token },
          body: formData
        });
        const data = await res.json();
        if (res.status !== 200) throw new Error(data.error || "Import failed");
        alert("Database restored successfully. Please refresh the page.\n\nYour data from just before this restore was automatically saved as a safety net — find it under Import Database if you need to undo this.");
      } catch(e) { alert("Import failed: " + e.message); }
    }
    setShowImportDialog(false);
  };

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", backgroundColor: "#f3f4f6", flexDirection: "row", paddingTop: isMob ? "48px" : "0" }}>

      {/* Mobile top bar */}
      {isMob && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: "48px", backgroundColor: "#14532d", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 12px", zIndex: 300 }}>
          <div style={{ color: "white", fontWeight: "bold", fontSize: "14px" }}>Golf Society Manager</div>
          <button onClick={() => setMenuOpen(!menuOpen)} style={{ background: "none", border: "none", color: "white", fontSize: "24px", cursor: "pointer", padding: "4px 8px", minHeight: "auto", lineHeight: 1 }}>
            {menuOpen ? "\u2715" : "\u2630"}
          </button>
        </div>
      )}

      {/* Overlay */}
      {isMob && menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", top: "48px", left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 199 }} />
      )}

      {/* Sidebar */}
      <div style={{
        width: isMob ? "280px" : "240px",
        backgroundColor: "#14532d",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        position: isMob ? "fixed" : "relative",
        top: isMob ? "48px" : "0",
        left: 0,
        bottom: 0,
        zIndex: 200,
        transform: isMob ? (menuOpen ? "translateX(0)" : "translateX(-100%)") : "none",
        transition: "transform 0.25s ease",
        overflowY: "auto",
      }}>
        {!isMob && (
          <div style={{ padding: "16px 14px 12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <div style={{ width: "34px", height: "34px", borderRadius: "50%", background: "radial-gradient(circle at 35% 30%, #ffffff, #d4d4d4 60%, #a3a3a3)", flexShrink: 0, position: "relative", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }}>
                <div style={{ position: "absolute", inset: 0, borderRadius: "50%", backgroundImage: "radial-gradient(circle, #9ca3af 1px, transparent 1.2px)", backgroundSize: "6px 6px", opacity: 0.5 }} />
              </div>
              <div>
                <div style={{ fontWeight: "800", fontSize: "16px", lineHeight: 1.1 }}>
                  <span style={{ color: "white" }}>Golf</span><span style={{ color: "#4ade80" }}>Society</span><span style={{ color: "white" }}>Manager</span>
                </div>
                <div style={{ color: "#86efac", fontSize: "11px", marginTop: "2px" }}>v{APP_VERSION}</div>
              </div>
            </div>
            {societyInfo?.name && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "rgba(255,255,255,0.06)", border: "1px solid #166534", borderRadius: "10px", padding: "10px 12px" }}>
                <span style={{ fontSize: "18px" }}>👥</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ color: "white", fontSize: "14px", fontWeight: "800" }}>{societyInfo.name}</div>
                  {societyInfo?.email && <div style={{ color: "#86efac", fontSize: "12px", marginTop: "2px", wordBreak: "break-all" }}>{societyInfo.email}</div>}
                </div>
              </div>
            )}
            {isPlayground && (
              <button onClick={handleResyncPlayground} disabled={resyncing}
                style={{ marginTop: "10px", width: "100%", background: "#7c3aed", color: "white", border: "none", borderRadius: "6px", padding: "8px 10px", fontSize: "12px", fontWeight: "700", cursor: "pointer", opacity: resyncing ? 0.6 : 1 }}>
                {resyncing ? "Resyncing..." : "🧪 Resync from Pin High"}
              </button>
            )}
          </div>
        )}
        <nav style={{ flex: 1, paddingTop: "4px", paddingBottom: "8px" }}>
          {/* Top pill actions — User Manual / Marker Verify Guide / Download Offline App.
              Redesign 2026-10-21 (Mike's own design, see [[gsm_sidebar_redesign_2026_10]]):
              rounded pill buttons with icon + chevron, not flat full-width bars. */}
          <div style={{ padding: "0 10px 12px", display: "flex", flexDirection: "column", gap: "8px" }}>
            {allNavItems.filter(item => (item.manual || item.guide || item.download) && !(item.hideForElectron && isElectron)).map(item => {
              const pillStyle = {
                display: "flex", alignItems: "center", gap: "10px", width: "100%", textAlign: "left",
                padding: "11px 14px", fontSize: "14px", fontWeight: "700", borderRadius: "10px", cursor: "pointer",
                textDecoration: "none", boxSizing: "border-box",
              };
              if (item.manual) {
                return (
                  <div key={item.path}>
                    <button onClick={() => handleNav(item.path, false)}
                      style={{ ...pillStyle, border: "none", backgroundColor: "#eab308", color: "#14532d" }}>
                      <span style={{ fontSize: "16px" }}>📖</span><span style={{ flex: 1 }}>User Manual</span><span>›</span>
                    </button>
                    <div style={{ padding: "4px 14px 0", fontSize: "11px", color: "#86efac", fontStyle: "italic" }}>
                      GSM is always growing — new features added regularly
                    </div>
                  </div>
                );
              }
              if (item.guide) {
                return (
                  <button key={item.path} onClick={() => handleNav(item.path, false)}
                    style={{ ...pillStyle, border: "none", backgroundColor: "#7dd3fc", color: "#0c4a6e" }}>
                    <span style={{ fontSize: "16px" }}>🧭</span><span style={{ flex: 1 }}>Marker Verify Guide</span><span>›</span>
                  </button>
                );
              }
              // download: a plain external link, not handleNav/navigate() --
              // points at the Microsoft Store listing, target="_blank" so
              // clicking it doesn't navigate the whole app away. Uses the
              // real, unmodified official Microsoft Store badge artwork
              // (get.microsoft.com) -- not a hand-drawn recreation -- shown
              // at a proper legible size below the label, stacked rather
              // than squeezed into one row.
              return (
                <a key={item.path} href={item.path} target="_blank" rel="noopener noreferrer"
                  style={{ ...pillStyle, flexDirection: "column", alignItems: "flex-start", gap: "8px", padding: "11px 14px", border: "1px solid #4ade80", backgroundColor: "transparent", color: "#dcfce7" }}>
                  <div style={{ display: "flex", width: "100%", alignItems: "center", gap: "10px" }}>
                    <div style={{ flex: 1 }}>
                      <div>Download Offline App</div>
                      <div style={{ fontSize: "10px", fontWeight: "400", color: "#86efac", fontStyle: "italic" }}>from the Official Microsoft Store</div>
                    </div>
                    <span>›</span>
                  </div>
                  <img src="https://get.microsoft.com/images/en-us%20dark.svg" alt="Official Microsoft Store" style={{ height: "44px" }} />
                </a>
              );
            })}
          </div>

          {NAV_SECTIONS.map(section => {
            const items = allNavItems.filter(item => item.section === section.key && !(item.hideForElectron && isElectron));
            if (!items.length) return null;
            const collapsed = !!collapsedSections[section.key];
            return (
              <div key={section.key} style={{ marginBottom: "4px" }}>
                <button onClick={() => toggleSection(section.key)}
                  style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "none", border: "none", cursor: "pointer", padding: "8px 16px", color: "#86efac", fontSize: "11px", fontWeight: "700", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                  <span>{section.label}</span><span style={{ fontSize: "12px" }}>{collapsed ? "⌄" : "⌃"}</span>
                </button>
                {!collapsed && items.map(item => {
                  const isActive = location.pathname === item.path;
                  const isDisabled = (item.teamOnly && !isTeamFormat) || (item.pairOnly && !isPairFormat);
                  return (
                    <button key={item.path} onClick={() => handleNav(item.path, isDisabled)}
                      style={{
                        width: "100%", display: "flex", alignItems: "center", gap: "12px", textAlign: "left",
                        padding: "10px 16px 10px 13px", fontSize: "14px", fontWeight: isActive ? "700" : "500",
                        border: "none", borderLeft: isActive ? "4px solid #4ade80" : "4px solid transparent",
                        cursor: isDisabled ? "not-allowed" : "pointer", minHeight: "44px",
                        backgroundColor: isActive ? "#16a34a" : "transparent",
                        color: isDisabled ? "#4ade80" : isActive ? "white" : "#dcfce7", opacity: isDisabled ? 0.5 : 1,
                      }}>
                      <span style={{ fontSize: "15px", width: "18px", textAlign: "center", flexShrink: 0, display: "inline-block", animation: item.path === "/wizard" ? "wizardSparkle 1.8s ease-in-out infinite" : "none" }}>{item.icon}</span>
                      {item.path === "/wizard" && <style>{"@keyframes wizardSparkle { 0%, 100% { transform: scale(1) rotate(0deg); filter: drop-shadow(0 0 0px #facc15); } 50% { transform: scale(1.25) rotate(-8deg); filter: drop-shadow(0 0 6px #facc15); } }"}</style>}
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div style={{ padding: "8px", borderTop: "1px solid #166534", display: "flex", flexDirection: "column", gap: "6px" }}>
          {isElectron && (
            <>
            <button onClick={() => setShowSyncModal(true)} disabled={syncing} style={{ width: "100%", backgroundColor: syncDone ? "#15803d" : "#0f766e", color: "white", fontSize: "12px", fontWeight: "600", borderRadius: "4px", padding: "8px 12px", border: "none", cursor: syncing ? "default" : "pointer", minHeight: "44px", opacity: syncing ? 1 : 1, position: "relative", overflow: "hidden" }}>
              {syncing && !syncDone && (
                <span style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
                  <span style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "40%", background: "rgba(255,255,255,0.25)", animation: "gsm-bar-slide 1.1s ease-in-out infinite" }} />
                </span>
              )}
              <span style={{ position: "relative" }}>
                {syncDone ? "✓ Synced!" : syncing ? `Syncing... ${syncSeconds}s` : "Sync"}
              </span>
            </button>
            {syncing && <style>{"@keyframes gsm-bar-slide { 0% { left: -40%; } 100% { left: 100%; } }"}</style>}
            </>
          )}
          {isElectron && lastSync && !syncing && (
            <div style={{ color: "#86efac", fontSize: "11px", textAlign: "center" }}>Last synced {lastSync.toLocaleTimeString()}</div>
          )}
          {isElectron && syncError && (
            <div style={{ color: "#fca5a5", fontSize: "11px", textAlign: "center" }}>{syncError}</div>
          )}
          {isElectron && window.electronAPI?.openGeneralPlayKiosk && (
            <button onClick={() => window.electronAPI.openGeneralPlayKiosk()} title="Opens the same kiosk screen members use, in its own window, for registering and scoring a General Play round on this PC" style={{ width: "100%", backgroundColor: "#16a34a", color: "white", fontSize: "12px", fontWeight: "600", borderRadius: "4px", padding: "8px 12px", border: "none", cursor: "pointer", minHeight: "44px" }}>⛳ Open General Play Kiosk</button>
          )}
          <button onClick={exportDatabase} style={{ width: "100%", backgroundColor: "#2563eb", color: "white", fontSize: "12px", fontWeight: "600", borderRadius: "4px", padding: "8px 12px", border: "none", cursor: "pointer", minHeight: "44px" }}>Export Database</button>
          <button onClick={importDatabase} style={{ width: "100%", backgroundColor: "#dc2626", color: "white", fontSize: "12px", fontWeight: "600", borderRadius: "4px", padding: "8px 12px", border: "none", cursor: "pointer", minHeight: "44px" }}>Import Database</button>
          <button onClick={exportGame} title="Send the current competition to a file — works like Save Game, for moving between the website and the offline app" style={{ width: "100%", backgroundColor: "#0891b2", color: "white", fontSize: "12px", fontWeight: "600", borderRadius: "4px", padding: "8px 12px", border: "none", cursor: "pointer", minHeight: "44px" }}>Export Game</button>
          <button onClick={importGame} title="Load a competition from a file — works like Load Game, creates a brand-new competition here" style={{ width: "100%", backgroundColor: "#7c3aed", color: "white", fontSize: "12px", fontWeight: "600", borderRadius: "4px", padding: "8px 12px", border: "none", cursor: "pointer", minHeight: "44px" }}>Import Game</button>
          <button onClick={() => handleNav("/change-password", false)} style={{ width: "100%", backgroundColor: "#ca8a04", color: "white", fontSize: "12px", fontWeight: "600", borderRadius: "4px", padding: "8px 12px", border: "none", cursor: "pointer", minHeight: "44px" }}>Change Password</button>
          <button onClick={handleLogout} style={{ width: "100%", backgroundColor: "#4b5563", color: "white", fontSize: "12px", fontWeight: "600", borderRadius: "4px", padding: "8px 12px", border: "none", cursor: "pointer", minHeight: "44px" }}>Logout</button>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
        {!dpaNoticeDismissed && (
          <div style={{ backgroundColor: "#eff6ff", borderBottom: "1px solid #bfdbfe", color: "#1e40af", padding: "8px 16px", fontSize: "12px", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", flexShrink: 0 }}>
            <span>Our Data Processing Agreement was updated ({DPA_VERSION}) to add encryption of names and email addresses within the live database, not only in backups. <a href="/data-processing-agreement" style={{ color: "#1e40af", textDecoration: "underline" }}>View the agreement</a></span>
            <button onClick={dismissDpaNotice} style={{ background: "rgba(30,64,175,0.1)", border: "none", color: "#1e40af", borderRadius: "4px", padding: "2px 8px", fontSize: "11px", fontWeight: 700, cursor: "pointer" }}>Dismiss</button>
          </div>
        )}
        {(!isOnline || pendingCount > 0) && (
          <div style={{
            backgroundColor: !isOnline ? "#b45309" : "#0f766e",
            color: "white", fontSize: "12px", fontWeight: 600, textAlign: "center",
            padding: "4px 8px", flexShrink: 0
          }}>
            {!isOnline
              ? "Offline" + (pendingCount > 0 ? ` — ${pendingCount} change${pendingCount === 1 ? "" : "s"} will sync when reconnected` : "")
              : `Syncing ${pendingCount} change${pendingCount === 1 ? "" : "s"}...`}
          </div>
        )}
        {location.pathname === "/results" && showGroupWinner && (
          <div style={{ backgroundColor: "#e5e7eb", borderBottom: "1px solid #d1d5db", padding: "8px 16px", display: "flex", gap: "8px", flexShrink: 0 }}>
            <button onClick={() => navigate("/results")}
              style={{ fontSize: "12px", fontWeight: "700", padding: "6px 14px", borderRadius: "6px", border: "1px solid #14532d", cursor: "pointer", backgroundColor: !location.search.includes("view=group") ? "#14532d" : "white", color: !location.search.includes("view=group") ? "white" : "#14532d" }}>
              Individual Winner
            </button>
            <button onClick={() => navigate("/results?view=group")}
              style={{ fontSize: "12px", fontWeight: "700", padding: "6px 14px", borderRadius: "6px", border: "1px solid #14532d", cursor: "pointer", backgroundColor: location.search.includes("view=group") ? "#14532d" : "white", color: location.search.includes("view=group") ? "white" : "#14532d" }}>
              Group Winner
            </button>
          </div>
        )}
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>{children}</div>
        <div style={{ backgroundColor: "#e5e7eb", padding: "4px" }}>
          <LegalFooter />
        </div>
      </div>

      {/* Sync Direction Modal */}
      {showSyncModal && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.7)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ backgroundColor: "white", borderRadius: "8px", padding: "24px", maxWidth: "440px", width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}>
            <div style={{ fontWeight: "700", fontSize: "16px", marginBottom: "12px", color: "#0f766e" }}>🔄 Sync — Choose a Direction</div>
            <div style={{ fontSize: "13px", marginBottom: "16px", color: "#6b7280", lineHeight: "1.6" }}>
              Sync only moves data one way at a time. Pick the direction that matches what you actually want to happen.
            </div>
            <button onClick={handlePull} style={{ width: "100%", textAlign: "left", backgroundColor: "#f0fdfa", border: "1px solid #0f766e", borderRadius: "6px", padding: "12px 14px", marginBottom: "10px", cursor: "pointer" }}>
              <div style={{ fontWeight: "700", fontSize: "14px", color: "#0f766e" }}>⬇ Pull from Website</div>
              <div style={{ fontSize: "12px", color: "#374151", marginTop: "2px" }}>Overwrite the data on THIS computer with what's currently on the website.</div>
            </button>
            <button onClick={handlePush} style={{ width: "100%", textAlign: "left", backgroundColor: "#ecfeff", border: "1px solid #0e7490", borderRadius: "6px", padding: "12px 14px", marginBottom: "16px", cursor: "pointer" }}>
              <div style={{ fontWeight: "700", fontSize: "14px", color: "#0e7490" }}>⬆ Push to Website</div>
              <div style={{ fontSize: "12px", color: "#374151", marginTop: "2px" }}>Overwrite the data on the WEBSITE with what's currently on this computer.</div>
            </button>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => setShowSyncModal(false)}
                style={{ padding: "8px 16px", borderRadius: "4px", border: "1px solid #d1d5db", cursor: "pointer", fontSize: "14px", backgroundColor: "white" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Dialog */}
      {showImportDialog && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.7)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ backgroundColor: "white", borderRadius: "8px", padding: "24px", maxWidth: "420px", width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}>
            <div style={{ fontWeight: "700", fontSize: "16px", marginBottom: "12px", color: "#1d4ed8" }}>📥 Import Database</div>
            <div style={{ fontSize: "13px", marginBottom: "16px", color: "#6b7280", lineHeight: "1.6" }}>
              Select a backup file (.json) to restore. This replaces your society's own data only — other societies on the platform are never affected. Before restoring, your current data is automatically saved as a safety net below, so this can always be undone.
            </div>
            {safetyNetFiles.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "12px", fontWeight: "600", color: "#374151", marginBottom: "6px" }}>Safety net (auto-saved before past restores)</div>
                <div style={{ maxHeight: "120px", overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: "4px" }}>
                  {safetyNetFiles.map(f => (
                    <div key={f.filename} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", fontSize: "12px", borderBottom: "1px solid #f3f4f6" }}>
                      <span style={{ color: "#374151" }}>{new Date(f.saved_at).toLocaleString('en-GB')}</span>
                      <button onClick={() => downloadSafetyNet(f.filename)}
                        style={{ padding: "3px 10px", borderRadius: "4px", border: "1px solid #d1d5db", cursor: "pointer", fontSize: "12px", backgroundColor: "white", color: "#2563eb" }}>
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => setShowImportDialog(false)}
                style={{ padding: "8px 16px", borderRadius: "4px", border: "1px solid #d1d5db", cursor: "pointer", fontSize: "14px", backgroundColor: "white" }}>
                Cancel
              </button>
              <label style={{ padding: "8px 16px", borderRadius: "4px", border: "none", cursor: "pointer", fontSize: "14px", backgroundColor: "#2563eb", color: "white", fontWeight: "600" }}>
                Choose File &amp; Import
                <input type="file" accept=".json" onChange={doImport} style={{ display: "none" }} />
              </label>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
